import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [currentChapter, setCurrentChapter] = useState(1);
  const [viewMode, setViewMode] = useState<'info' | 'reading'>('info');

  useEffect(() => {
    if (comic) {
      setCurrentChapter(1);
      setViewMode('info');
    }
  }, [id, comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const chapter = comic.chapters.find((ch) => ch.chapterNumber === currentChapter) || comic.chapters[comic.chapters.length - 1];
  const readingChapter = chapter;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '动漫之家',
    },
  };

  const generateReadingContent = () => {
    const paragraphs = [
      `深夜的首尔，霓虹灯在雨幕中闪烁，整座城市笼罩在一层朦胧的薄雾之中。`,
      `"你真的要这么做吗？"她的声音在电话那头微微颤抖，带着一丝不敢置信。`,
      `他深吸一口气，望着窗外模糊的街景，雨水顺着玻璃缓缓滑落，像是时间的痕迹。`,
      `"我没有别的选择了。"他的声音很轻，却异常坚定。`,
      `电话那头沉默了许久，久到他以为她已经挂断了。`,
      `"好，我等你。"终于，她的声音再次响起，这次却没有了颤抖。`,
      `他闭上眼睛，脑海中浮现出她的笑容。那个在樱花树下转身回眸的瞬间，定格在了他的记忆中。`,
      `画面一转，时间回到三个月前。`,
      `春日的校园里，樱花正盛。粉色的花瓣随着微风飘落，铺满了整条林荫道。`,
      `"对不起！"一个女生的声音响起，伴随着书本散落一地的声响。`,
      `他低头看着被撞散的画稿，正准备发火，却在看到对方的那一刻愣住了。`,
      `阳光透过樱花树的缝隙洒在她身上，她的眼睛里闪烁着惊慌和歉意。那一刻，他忽然觉得所有的愤怒都烟消云散了。`,
      `"没关系。"他蹲下身，帮她一起捡起散落的画稿。`,
      `她的手指纤细修长，翻动着他的画稿时格外轻柔。`,
      `"这些都是你画的吗？"她的眼睛亮了起来，"好厉害！"`,
      `他有些不好意思地点了点头，耳朵不自觉地红了起来。`,
      `"我叫林小月，美术系大一新生。"她伸出手，笑容灿烂。`,
      `"金秀贤，漫画社社长。"他握住她的手，感受到她掌心的温度。`,
      `从那天起，他的生活轨迹彻底改变了。`,
      `两个月后，他们的关系从陌生到熟悉，从朋友到恋人。每一个周末，他们都会在这条樱花道下散步，谈论着各自的梦想和未来。`,
      `然而命运总是喜欢在最美的时刻给人重重一击。`,
      `"我要去美国了。"那天，林小月突然说道，眼神中带着复杂。`,
      `金秀贤愣住了，手中的画笔掉落在地上，发出清脆的声响。`,
      `"下周就走，交换生项目，为期一年。"她的声音很轻，却像重锤一样敲在他的心上。`,
      `他想说些什么，却发现自己什么也说不出口。`,
      `"我不想去的。"她的眼眶红了，"可是这是我爸妈的期望，我..."`,
      `"去吧。"他打断了她的解释，努力挤出一个笑容，"一年而已，很快就过去了。"`,
      `他不知道自己是怎么说出这句话的，明明心里疼得要命。`,
      `离别的那天，他没有去送机。他害怕自己在机场失态，害怕自己会不顾一切地让她留下。`,
      `他只在画室里，一遍又一遍地画着她的肖像。`,
      `而今天，是她离开后的第七十三天。`,
      `窗外的雨还在下，他拿起手机，看着屏幕上她的照片。那是他们第一次约会时拍的，背景是满开的樱花。`,
      `"再等两百九十一天。"他轻声说着，嘴角微微上扬。`,
      `这段跨越太平洋的爱情，才刚刚开始。`,
    ];
    return paragraphs;
  };

  const paragraphs = generateReadingContent();

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} | 韩国漫画在线免费阅读`}
        description={comic.description}
        keywords={`${comic.title},${comic.categoryName},韩国漫画,无删减漫画,下拉式漫画,韩漫在线免费阅读,动漫之家`}
        canonicalUrl={`https://dongmanzhijia.com/comic/${comic.id}`}
        ogTitle={`${comic.title} - 韩国漫画在线免费阅读`}
        ogDescription={comic.description}
        ogImage={comic.coverUrl}
        ogType="article"
        schemaData={[articleSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '漫画分类', href: '/category' },
                  { label: comic.categoryName, href: `/category/${categories.find(c => c.id === comic.categoryId)?.slug || ''}` },
                  { label: comic.title },
                ]}
              />
            </div>
          </div>

          {viewMode === 'info' ? (
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div className="flex flex-col md:flex-row gap-8 md:gap-10">
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-[200px] md:w-[260px] aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.coverUrl}
                      alt={`${comic.title} - 韩国漫画`}
                      title={`${comic.title} - 韩国漫画在线免费阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-600">分类：<Link to={`/category/${categories.find(c => c.id === comic.categoryId)?.slug}`} className="text-primary-500 hover:text-primary-600">{comic.categoryName}</Link></span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-500' : 'text-foreground-500'}`}>
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mb-5">
                    <span className="flex items-center gap-1 text-sm text-accent-600 font-semibold">
                      <i className="ri-star-fill"></i> {comic.rating}
                    </span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(1)}万 阅读
                    </span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-file-list-line mr-1"></i>{comic.chapterCount} 话
                    </span>
                    <span className="text-sm text-foreground-500">
                      更新于 {comic.updatedAt}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-xl">
                    {comic.description}
                  </p>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      onClick={() => setViewMode('reading')}
                      className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      开始阅读 第{comic.chapters[comic.chapters.length - 1].chapterNumber}话
                    </button>
                    <button
                      onClick={() => {
                        setCurrentChapter(1);
                        setViewMode('reading');
                      }}
                      className="px-8 py-3 rounded-full border border-background-300 text-foreground-700 font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      从第1话开始
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">章节列表</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {comic.chapters.slice().reverse().map((ch) => (
                    <button
                      key={ch.id}
                      onClick={() => {
                        setCurrentChapter(ch.chapterNumber);
                        setViewMode('reading');
                      }}
                      className="px-4 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors text-left whitespace-nowrap cursor-pointer"
                    >
                      {ch.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 py-8">
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => setViewMode('info')}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回详情
                </button>
                <h2 className="text-lg font-heading font-bold text-foreground-800">
                  {comic.title} - {readingChapter.title}
                </h2>
                <span className="text-xs text-foreground-400 whitespace-nowrap">
                  {currentChapter}/{comic.chapterCount} 话
                </span>
              </div>

              <article className="prose prose-sm max-w-none">
                {paragraphs.map((text, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
                    {text}
                  </p>
                ))}
              </article>

              <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200">
                <button
                  onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
                  disabled={currentChapter <= 1}
                  className={`flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    currentChapter <= 1
                      ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-arrow-left-line"></i> 上一话
                </button>
                <span className="text-sm text-foreground-500">
                  {readingChapter.title}
                </span>
                <button
                  onClick={() => setCurrentChapter(Math.min(comic.chapterCount, currentChapter + 1))}
                  disabled={currentChapter >= comic.chapterCount}
                  className={`flex items-center gap-1 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    currentChapter >= comic.chapterCount
                      ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  下一话 <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}

const categories = [
  { id: 'cat-1', name: '恋爱', slug: 'romance' },
  { id: 'cat-2', name: '奇幻', slug: 'fantasy' },
  { id: 'cat-3', name: '动作', slug: 'action' },
  { id: 'cat-4', name: '悬疑', slug: 'mystery' },
  { id: 'cat-5', name: '搞笑', slug: 'comedy' },
  { id: 'cat-6', name: '恐怖', slug: 'horror' },
  { id: 'cat-7', name: '剧情', slug: 'drama' },
  { id: 'cat-8', name: '日常', slug: 'slice-of-life' },
  { id: 'cat-9', name: '穿越', slug: 'isekai' },
  { id: 'cat-10', name: '校园', slug: 'school' },
];